import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import LendyIcon from "../components/lendy/LendyIcon";
import "./plusMenu.css";

const MENU_WIDTH = 252;
const VIEWPORT_MARGIN = 12;
const ANCHOR_GAP = 6;

/** Above the + button, lined up with its left edge, kept inside the sidebar. */
const getMenuPosition = (anchor) => {
  const { top, left } = anchor.getBoundingClientRect();
  const width = Math.min(MENU_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
  const maxLeft = window.innerWidth - VIEWPORT_MARGIN - width;

  return { bottom: window.innerHeight - top + ANCHOR_GAP, left: Math.max(VIEWPORT_MARGIN, Math.min(left, maxLeft)), width };
};

const getMenuItems = (menu) => Array.from(menu?.querySelectorAll('[role="menuitem"]') || []);

const getNextIndex = (key, index, count) => {
  if (key === "ArrowDown") {
    return (index + 1) % count;
  }
  if (key === "ArrowUp") {
    return (index - 1 + count) % count;
  }
  if (key === "Home") {
    return 0;
  }
  if (key === "End") {
    return count - 1;
  }
  return null;
};

/** The composer's + menu, as in the classic chat: chat with the loan's documents, or attach files. */
const PlusMenu = ({ anchor, items, onClose }) => {
  const menuRef = useRef(null);
  // The parent passes a new onClose each render; the listeners below always call the latest.
  const onCloseRef = useRef(onClose);
  const [position] = useState(() => getMenuPosition(anchor));

  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    const [firstItem] = getMenuItems(menuRef.current);
    firstItem?.focus();
  }, []);

  useEffect(() => {
    const close = () => onCloseRef.current();
    const closeOnOutsideClick = (event) => {
      if (!menuRef.current?.contains(event.target) && !anchor.contains(event.target)) {
        close();
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        close();
        anchor.focus?.();
        return;
      }

      const menuItems = getMenuItems(menuRef.current);
      const nextIndex = getNextIndex(event.key, menuItems.indexOf(document.activeElement), menuItems.length);
      if (nextIndex === null || !menuItems.length) {
        return;
      }
      event.preventDefault();
      menuItems[nextIndex].focus();
    };
    document.addEventListener("mousedown", closeOnOutsideClick);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", close);

    return () => {
      document.removeEventListener("mousedown", closeOnOutsideClick);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", close);
    };
  }, [anchor]);

  const renderItem = ({ id, icon, label, hint, onSelect }) => (
    <button
      key={id}
      type="button"
      role="menuitem"
      className="plus-menu-item"
      onClick={() => {
        onClose();
        onSelect();
      }}
    >
      <span className="plus-menu-icon" aria-hidden="true">
        <LendyIcon name={icon} size={17} />
      </span>
      <span className="plus-menu-text">
        <b>{label}</b>
        <small>{hint}</small>
      </span>
    </button>
  );

  return createPortal(
    <div ref={menuRef} className="plus-menu" role="menu" aria-label="Attach or chat with docs" style={position}>
      {items.map(renderItem)}
    </div>,
    document.body
  );
};

export default PlusMenu;
